import { useState, type ReactNode } from 'react';
import { DownloadIcon, TableIcon, BarChart3Icon } from 'lucide-react';
import { btnGhost } from '../../ui/styles';
import { downloadCsv } from '../../../utils/download';
import { toCsv } from '../../../core/rules/csv';
import { DataTable, csvRows, type TableData } from './DataTable';
type Props = {
  title: string;
  description?: string;
  table: TableData;
  /** Zonder `.csv`; de periode komt er vanzelf achter. */
  fileName: string;
  /** Alleen een tabel (geen grafiek om tussen te wisselen). */
  tableOnly?: boolean;
  className?: string;
  children?: ReactNode;
};

/**
 * Een blok met een grafiek, met de cijfers als tabel ernaast: "Als tabel" (zichtbaar, voor
 * iedereen) en "CSV" (dezelfde cijfers voor Excel). Voor schermlezers staat de tabel er altijd.
 */
export function ChartFrame({
  title,
  description,
  table,
  fileName,
  tableOnly = false,
  className = '',
  children
}: Props) {
  const [asTable, setAsTable] = useState(false);
  const showTable = tableOnly || asTable;
  return <section className={`rounded-lg border border-line bg-white p-4 sm:p-6 ${className}`} data-id="element-1772" data-mp="2ct5ghernzmge">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-1773" data-mp="2hqouqeod3z8c">
        <div className="min-w-0" data-id="element-1774" data-mp="2mhnczjswv7e8">
          <h2 className="text-[15px] font-medium text-ink" data-id="element-1775" data-mp="2mjhyp0c0b3jd">{title}</h2>
          {description && <p className="mt-0.5 text-[13px] leading-snug text-muted" data-id="element-1776" data-mp="2rg8fxqzjs56d">{description}</p>}
        </div>
        <div className="-mr-2 flex items-center gap-1" data-id="element-1777" data-mp="1tuje7wkevpaw">
          {!tableOnly && <button type="button" onClick={() => setAsTable(v => !v)} aria-pressed={asTable} className={btnGhost} data-id="element-1778" data-mp="1yrt1p6o8pqw3">
              {asTable ? <BarChart3Icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1779" data-mp="23q4bjhejc1eb" /> : <TableIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1780" data-mp="27uc1fhwqbne4" />}
              {asTable ? 'Als grafiek' : 'Als tabel'}
            </button>}
          <button type="button" onClick={() => downloadCsv(`${fileName}.csv`, toCsv(table.columns.map(c => c.label), csvRows(table)))} disabled={table.rows.length === 0} className={`${btnGhost} disabled:opacity-40`} data-id="element-1781" data-mp="3j866z4xx7cfl">
            <DownloadIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1782" data-mp="1f6xu028tdn7u" />
            CSV
            <span className="sr-only" data-id="element-1783" data-mp="1o59oifcsx55k"> van {title}</span>
          </button>
        </div>
      </div>
      {showTable ? <DataTable {...table} caption={title} data-id="element-1784" data-mp="10f6y03ojc792" /> : <>
          <div aria-hidden="true" data-id="element-1785" data-mp="we3n7vlch1ep">{children}</div>
          <DataTable {...table} caption={title} className="sr-only" data-id="element-1786" data-mp="wfenvmjhtt1l" />
        </>}
    </section>;
}